import { Button, Typography, theme } from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import Icon from "@ant-design/icons";
import { Icons } from "../assets/icons";
import { useGuard } from "../Guard";

export interface LiveNotificationToastsProps {
  readonly onOpen?: () => void;
}

interface Toast {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
}

const DISMISS_MS = 5000;
const MAX_VISIBLE = 3; // cap the on-screen stack; newer pushes the oldest out

/**
 * Renders the ephemeral "incoming push" toasts as a self-contained, fixed bottom-right stack.
 *
 * Why not antd `notification`? Its rc-motion enter animation re-renders the notice through several
 * phases (prepare → start → active), which on antd 5.18 + `cssVar` shows up as a 3–4× "quickfire"
 * flash before settling. Here each card animates in with ONE GPU-composited `animate.css` keyframe
 * (transform/opacity only) and is never re-rendered after mount (keyed by uid) — so no jitter.
 *
 * The component owns its own hub subscription + state and is `memo`'d with a stable `onOpen`, so it
 * never re-renders when the surrounding header (badge / drawer / liveness) updates.
 */
const LiveNotificationToasts = memo(function LiveNotificationToasts(
  props: LiveNotificationToastsProps,
) {
  const { token } = theme.useToken();
  const { on, off } = useGuard();
  const { onOpen } = props;
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((uid: string) => {
    setToasts((current) => current.filter((t) => t.uid !== uid));
    const timer = timers.current.get(uid);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(uid);
    }
  }, []);

  useEffect(() => {
    const handler = (
      recipientUid: string,
      _notificationUid: string,
      title: string,
      message: string,
    ) => {
      // Prepend (newest on top), de-dupe by uid, and cap the stack at MAX_VISIBLE — overflow (the
      // oldest) is dropped and its timer cleared.
      setToasts((current) => {
        const next = [
          { uid: recipientUid, title, message },
          ...current.filter((t) => t.uid !== recipientUid),
        ];
        next.slice(MAX_VISIBLE).forEach((t) => {
          const timer = timers.current.get(t.uid);
          if (timer) {
            clearTimeout(timer);
            timers.current.delete(t.uid);
          }
        });
        return next.slice(0, MAX_VISIBLE);
      });

      const existing = timers.current.get(recipientUid);
      if (existing) clearTimeout(existing);
      timers.current.set(
        recipientUid,
        setTimeout(() => remove(recipientUid), DISMISS_MS),
      );
    };

    on("ReceiveNotification", handler);
    return () => off("ReceiveNotification", handler);
  }, [on, off, remove]);

  // Clear any pending timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  if (!toasts.length) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style={{ pointerEvents: "none" }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.uid}
          className="animate__animated animate__fadeInRight flex w-[440px] max-w-[90vw] cursor-pointer flex-row items-start gap-2 p-4"
          style={{
            pointerEvents: "auto",
            backgroundColor: token.colorBgElevated,
            borderRadius: token.borderRadius,
            boxShadow: token.boxShadowSecondary,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
          onClick={() => {
            onOpen?.();
            remove(toast.uid);
          }}
        >
          <div className="flex flex-auto flex-col overflow-hidden">
            <Typography.Text strong ellipsis={{ tooltip: toast.title }}>
              {toast.title}
            </Typography.Text>
            <Typography.Paragraph
              type="secondary"
              className="mb-0 mt-1"
              ellipsis={{ rows: 4, tooltip: toast.message }}
            >
              {toast.message}
            </Typography.Paragraph>
          </div>
          <Button
            type="text"
            size="small"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              remove(toast.uid);
            }}
            icon={<Icon className="text-lg" component={Icons.CloseIcon} />}
          />
        </div>
      ))}
    </div>
  );
});

export default LiveNotificationToasts;
